/**
 * Text State Management Utilities
 * Helpers for managing text editing state with debouncing
 */

/**
 * Get current text state from either tempText or dimension's html_text
 * Single source of truth - prefers tempText during active editing
 * @param {Object|null} tempText - Temporary text state during editing
 * @param {Object|null} dimension - The dimension object containing html_text
 * @returns {Object} Current text state with text, charStyleOverrides, styleOverrides, css
 */
export function getCurrentTextState(tempText, dimension) {
  if (tempText?.text !== undefined) {
    return { ...tempText };
  }
  return dimension?.html_text
    ? { ...dimension.html_text }
    : { text: '', charStyleOverrides: [], styleOverrides: [], css: {} };
}

/**
 * Creates a debounced text updater to reduce state updates during rapid typing
 * @param {Function} setTempText - State setter for tempText
 * @param {Function} setUnSavedChanges - State setter for unsaved changes flag
 * @param {number} delay - Debounce delay in milliseconds (default: 150ms)
 * @returns {Object} Object with update() for debounced updates and flush() for immediate updates
 */
export function createDebouncedTextUpdater(setTempText, setUnSavedChanges, delay = 150) {
  let timeoutId = null;
  let pendingState = null;

  return {
    /**
     * Queue a debounced update - will be applied after `delay` ms of inactivity
     * @param {Object} newState - The new text state to set
     */
    update(newState) {
      pendingState = newState;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (pendingState) {
          setTempText(pendingState);
          setUnSavedChanges(true);
          pendingState = null;
        }
        timeoutId = null;
      }, delay);
    },

    /**
     * Immediately apply any pending update (useful before save or blur)
     */
    flush() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (pendingState) {
        setTempText(pendingState);
        setUnSavedChanges(true);
        pendingState = null;
      }
    },

    /**
     * Cancel any pending update without applying it
     */
    cancel() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      pendingState = null;
    },

    /**
     * Check if there's a pending update
     * @returns {boolean}
     */
    hasPending() {
      return pendingState !== null;
    },

    /**
     * Get the pending state without flushing
     * Useful for reading the latest value before async operations
     * @returns {Object|null} The pending state or null if no pending update
     */
    getPending() {
      return pendingState;
    }
  };
}
